import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Plus, ArrowRight, Download } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { getInvoices, downloadInvoicePdf } from '../services/api'
import { formatDate } from '../lib/formatters'
import StatusBadge from '../components/ui/StatusBadge'

/**
 * Landing page for the restricted INVOICE_CREATOR role. Deliberately shows NO
 * financial information — no amounts, balances, totals or expenses. Only lets the
 * user raise a new invoice and find + download the PDFs of invoices they created.
 */
export default function CreatorHome() {
  const { user } = useAuth()
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloadingId, setDownloadingId] = useState(null)

  useEffect(() => {
    getInvoices({ take: 8 })
      .then((res) => setInvoices(res.data.invoices || []))
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false))
  }, [])

  async function handleDownload(inv) {
    if (inv.status === 'DRAFT') return
    setDownloadingId(inv.id)
    try {
      const res = await downloadInvoicePdf(inv.id)
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `${inv.number}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (_) {
      alert('Could not download the PDF. It may still be pending admin approval.')
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          Welcome, {user?.name?.split(' ')[0] || 'there'}
        </h1>
        <p className="text-gray-500 mt-1">Raise invoices and download their PDFs once approved.</p>
      </div>

      {/* Primary action */}
      <Link
        to="/invoices/new"
        className="card flex items-center gap-4 hover:border-brand-300 hover:shadow-md transition-all group cursor-pointer"
      >
        <div className="p-3 rounded-lg bg-brand-50 text-brand-600 group-hover:bg-brand-100 transition-colors">
          <FileText className="h-6 w-6" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">Create Invoice</p>
          <p className="text-xs text-gray-500">Draft for admin approval</p>
        </div>
        <Plus className="h-5 w-5 text-gray-300 ml-auto group-hover:text-brand-500 transition-colors" />
      </Link>

      {/* My invoices — status + download only, no amounts */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">My Invoices</h2>
          <Link to="/invoices" className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {loading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">You haven't created any invoices yet.</p>
        ) : (
          <div className="space-y-2">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Link to={`/invoices/${inv.id}`} className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{inv.number || 'DRAFT'}</p>
                  <p className="text-xs text-gray-400 truncate">{inv.customer?.name} · {formatDate(inv.date)}</p>
                </Link>
                <div className="flex items-center gap-3">
                  <StatusBadge status={inv.status} />
                  {inv.status !== 'DRAFT' && (
                    <button
                      onClick={() => handleDownload(inv)}
                      disabled={downloadingId === inv.id}
                      className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
                      title="Download PDF"
                    >
                      <Download className="h-3.5 w-3.5" />
                      {downloadingId === inv.id ? '…' : 'PDF'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
